import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { fmtUSDate } from "../lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Banknote, Loader2, Plus, Trash2, CheckCircle2 } from "lucide-react";

export const PAYMENT_METHODS = [
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "ach", label: "ACH" },
  { value: "credit_card", label: "Credit Card" },
  { value: "zelle", label: "Zelle" },
  { value: "cashapp", label: "CashApp" },
  { value: "money_order", label: "Money Order" },
  { value: "other", label: "Other" },
];

export function methodLabel(method) {
  return PAYMENT_METHODS.find((m) => m.value === method)?.label || method;
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function VendorPaymentsDialog({ vendor, onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    payment_method: "check",
    check_number: "",
    paid_at: todayIso(),
    notes: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/vendors/${vendor.id}/payments`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load payments");
      setItems([]);
    }
  };

  useEffect(() => { if (vendor?.id) load(); /* eslint-disable-next-line */ }, [vendor?.id]);

  const total = (items || []).reduce((s, p) => s + (p.amount || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      await api.post(`/vendors/${vendor.id}/payments`, {
        amount: amt,
        payment_method: form.payment_method,
        check_number: form.check_number,
        paid_at: form.paid_at,
        notes: form.notes,
      });
      toast.success("Payment recorded");
      setForm({ amount: "", payment_method: "check", check_number: "", paid_at: todayIso(), notes: "" });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to record payment");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this payment record?")) return;
    try {
      await api.delete(`/vendors/${vendor.id}/payments/${pid}`);
      toast.success("Payment deleted");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const showCheckField = form.payment_method === "check" || form.payment_method === "money_order";

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vendor-payments-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-700" />
            Payments — {vendor?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3 mb-4 flex items-center justify-between">
          <div>
            <div className="overline text-emerald-700">Total paid to vendor</div>
            <div className="font-mono text-2xl font-bold text-slate-900" data-testid="vendor-payments-total">
              ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="text-xs text-emerald-800">
            {(items?.length || 0)} payment{(items?.length || 0) === 1 ? "" : "s"} on file
          </div>
        </div>

        <form onSubmit={submit} className="border border-slate-200 rounded-sm p-4 mb-4 space-y-3" data-testid="vendor-payment-form">
          <div className="overline text-slate-600">Record a new payment</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount paid (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="input-clean w-full pl-7"
                  data-testid="vendor-payment-amount"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </Field>
            <Field label="Payment method">
              <select
                className="input-clean w-full"
                data-testid="vendor-payment-method"
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              >
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {showCheckField ? (
              <Field label="Check number">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="vendor-payment-check-number"
                  value={form.check_number}
                  onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                  placeholder="e.g. 1042"
                />
              </Field>
            ) : (
              <Field label="Reference (optional)">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="vendor-payment-reference"
                  value={form.check_number}
                  onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                  placeholder="Confirmation # / memo"
                />
              </Field>
            )}
            <Field label="Date paid">
              <input
                required
                type="date"
                className="input-clean w-full"
                data-testid="vendor-payment-date"
                value={form.paid_at}
                onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea
              rows={2}
              maxLength={500}
              className="input-clean w-full text-sm"
              data-testid="vendor-payment-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What was this payment for?"
            />
          </Field>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy}
              data-testid="vendor-payment-submit"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Record payment
            </button>
          </div>
        </form>

        <div className="border border-slate-200 rounded-sm">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Payment history
          </div>
          {items === null ? (
            <div className="p-8 flex items-center justify-center text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm" data-testid="vendor-payments-empty">
              No payments recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-50/50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Method</th>
                  <th className="text-left px-4 py-2">Check #</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-right px-4 py-2"> </th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`vendor-payment-row-${p.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{fmtUSDate(p.paid_at)}</td>
                    <td className="px-4 py-2 text-slate-700">{methodLabel(p.payment_method)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{p.check_number || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">
                      ${(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => remove(p.id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Delete"
                        data-testid={`vendor-payment-delete-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <button type="button" className="btn-outline-dark" onClick={onClose} data-testid="vendor-payments-close">
            <CheckCircle2 className="w-4 h-4 mr-1.5 inline" /> Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
